import React, { useState } from 'react'
import { Modal, Form, Input, Rate, Button, message, Select } from 'antd'
import { BulbOutlined } from '@ant-design/icons'
import { useAuth } from '../../contexts/AuthContext'
import { collection, addDoc } from 'firebase/firestore'
import { db } from '../../services/firebase'

const { TextArea } = Input
const { Option } = Select

const FeedbackModal = ({ visible, onClose }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { userProfile } = useAuth()

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()
      
      const feedbackData = {
        ...values,
        userId: userProfile?.uid,
        userName: userProfile?.name || 'Anonymous',
        userEmail: userProfile?.email || '',
        pincode: userProfile?.allowedPincodes?.[0] || '',
        createdAt: new Date(),
        status: 'pending',
        resolved: false
      }

      await addDoc(collection(db, 'feedback'), feedbackData)
      
      message.success('Thank you for your feedback! We appreciate your input.')
      form.resetFields()
      onClose()
    } catch (error) {
      console.error('Error submitting feedback:', error)
      message.error('Failed to submit feedback. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'improvement', label: 'Improvement Suggestion' },
    { value: 'usability', label: 'Usability Issue' },
    { value: 'performance', label: 'Performance Issue' },
    { value: 'other', label: 'Other' }
  ]

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BulbOutlined style={{ color: '#1890ff' }} />
          Share Your Feedback
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Submit Feedback
        </Button>
      ]}
      width={600}
      centered
      styles={{ 
        body: { 
          maxHeight: 'calc(90vh - 110px)',
          overflowY: 'auto'
        } 
      }}
    >
      <div style={{ marginBottom: '16px', color: '#666' }}>
        Help us improve AUMS by sharing your thoughts, suggestions, or reporting issues.
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          rating: 5,
          type: 'improvement'
        }}
      >
        <Form.Item
          name="type"
          label="Feedback Type"
          rules={[{ required: true, message: 'Please select feedback type!' }]}
        >
          <Select placeholder="Select feedback type">
            {feedbackTypes.map(type => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Please enter a title!' },
            { min: 5, message: 'Title must be at least 5 characters!' }
          ]}
        >
          <Input placeholder="Brief description of your feedback" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: 'Please provide details!' },
            { min: 20, message: 'Description must be at least 20 characters!' }
          ]}
        >
          <TextArea
            placeholder="Please provide detailed information about your feedback, including steps to reproduce if it's a bug."
            rows={6}
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          name="rating"
          label="Overall Experience Rating"
        >
          <Rate
            character="⭐"
            tooltips={['Very Poor', 'Poor', 'Average', 'Good', 'Excellent']}
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: 'Please select priority!' }]}
        >
          <Select placeholder="How urgent is this feedback?">
            <Option value="low">Low - Nice to have</Option>
            <Option value="medium">Medium - Important</Option>
            <Option value="high">High - Critical</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="contactMe"
          label="Contact Preference"
        >
          <Select placeholder="Would you like us to contact you about this feedback?">
            <Option value="yes">Yes, please contact me</Option>
            <Option value="no">No, just submit anonymously</Option>
            <Option value="updates">Only contact me with updates</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default FeedbackModal
